import { useQuery } from '@tanstack/react-query'
import { getReleasesCatalog, getTenants } from '@/services'
import { AppButton, EmptyState, IconButton, Skeleton, TableSkeleton } from '@/components/platform/primitives'
import { formatDate } from '@/lib/utils'

function ReleasesSkeleton() {
  return (
    <>
      <div className="card" style={{ marginBottom: 18, background: 'var(--bisne-dark)', borderColor: 'var(--bisne-dark)' }}>
        <div style={{ padding: 22 }}>
          <Skeleton width={180} height={22} radius={999} style={{ marginBottom: 16 }} />
          <Skeleton width={260} height={42} style={{ marginBottom: 18 }} />
          <Skeleton width="100%" height={62} />
        </div>
      </div>
      <div className="card">
        <div className="card-header"><h3>Historial</h3></div>
        <div className="card-body flush"><TableSkeleton columns={6} rows={7} /></div>
      </div>
    </>
  )
}

export function ReleasesPage() {
  const { data: releases = [], isLoading } = useQuery({
    queryKey: ['releases-catalog'],
    queryFn: getReleasesCatalog,
  })
  const { data: tenantsResult } = useQuery({
    queryKey: ['tenants', 'all-for-adoption'],
    queryFn: () => getTenants({ pageSize: 200 }),
  })

  const tenants = tenantsResult?.data || []
  const totalTenants = tenants.length
  const adoptionFor = (tag: string) =>
    tenants.filter((t) => (t.current_version || t.tenant_version) === tag).length

  const current = releases[0]

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Plataforma</div>
          <h1>Releases</h1>
          <div className="subtitle">Las versiones las publica el CI. El panel solo las muestra y permite asignarlas a tenants.</div>
        </div>
        <div className="page-actions">
          <AppButton variant="secondary" icon="external">Ver CI</AppButton>
        </div>
      </div>

      {isLoading ? (
        <ReleasesSkeleton />
      ) : releases.length === 0 ? (
        <EmptyState icon="package" title="Sin releases" desc="Todavía no se registró ninguna versión desde el CI." />
      ) : (
        <>
          {current ? (
            <div className="card" style={{ marginBottom: 18, background: 'var(--bisne-dark)', color: 'var(--text-inverse)', borderColor: 'var(--bisne-dark)' }}>
              <div style={{ padding: 22 }}>
                <div className="row gap-3" style={{ alignItems: 'center' }}>
                  <span className="badge badge-success badge-sm"><span className="dot" />Stable más reciente</span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-inverse-2)', opacity: 0.7 }}>Publicada el {formatDate(current.promoted_at || current.created_at)}</span>
                </div>
                <div className="row gap-3" style={{ marginTop: 14, alignItems: 'baseline' }}>
                  <span style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 38, letterSpacing: '-0.02em', color: 'var(--bisne-green)' }}>{current.release_tag}</span>
                  <span style={{ fontSize: 14, color: 'var(--text-inverse-2)', fontFamily: 'var(--font-mono)' }}>{current.source_ref || current.short_sha || current.channel}</span>
                </div>
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, fontSize: 12.5 }}>
                  {[
                    { label: 'Tenant API', value: current.tenant_api_image },
                    { label: 'Tenant admin', value: current.tenant_admin_image },
                    { label: 'Tenant site', value: current.tenant_storefront_image || current.tenant_web_image },
                  ].map((cell) => (
                    <div key={cell.label}>
                      <div style={{ color: 'var(--text-inverse-2)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10.5, fontWeight: 600 }}>{cell.label}</div>
                      <div className="t-mono" style={{ marginTop: 4, wordBreak: 'break-all' }}>{cell.value || '—'}</div>
                    </div>
                  ))}
                  <div>
                    <div style={{ color: 'var(--text-inverse-2)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10.5, fontWeight: 600 }}>Tenants en esta versión</div>
                    <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 18, marginTop: 2, color: 'var(--bisne-green)' }}>{adoptionFor(current.release_tag)} de {totalTenants}</div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="card">
            <div className="card-header"><h3>Historial</h3></div>
            <div className="card-body flush">
              <table className="table">
                <thead>
                  <tr>
                    <th>Tag</th>
                    <th>Canal</th>
                    <th>Publicada</th>
                    <th>Notas</th>
                    <th>Adopción</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {releases.map((release, index) => {
                    const tenantsOn = adoptionFor(release.release_tag)
                    const adoptionPct = totalTenants > 0 ? (tenantsOn / totalTenants) * 100 : 0
                    return (
                      <tr key={release.id}>
                        <td>
                          <div className="row gap-2">
                            <span className="code-chip" style={{ fontWeight: 600 }}>{release.release_tag}</span>
                            {index === 0 ? <span className="badge badge-success badge-sm">Más reciente</span> : null}
                          </div>
                        </td>
                        <td><span className="badge badge-outline badge-sm">{release.channel}</span></td>
                        <td><span style={{ fontSize: 12 }}>{formatDate(release.promoted_at || release.created_at)}</span></td>
                        <td style={{ maxWidth: 320 }}><span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{release.source_ref || release.commit_sha || 'Release registrada'}</span></td>
                        <td>
                          <div className="row gap-2" style={{ alignItems: 'center' }}>
                            <span className="tabular" style={{ fontSize: 12, fontWeight: 600, minWidth: 38 }}>{tenantsOn}/{totalTenants}</span>
                            <div style={{ width: 60, height: 4, background: 'var(--surface-3)', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{ width: `${adoptionPct}%`, height: '100%', background: 'var(--bisne-green)' }} />
                            </div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {release.run_url
                            ? <a href={release.run_url} target="_blank" rel="noreferrer"><IconButton icon="external" tooltip="Release notes" size={13} /></a>
                            : <IconButton icon="external" tooltip="Sin enlace" size={13} />}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  )
}
